/**
 * Web counterpart of loopit-app/lib/referralAttribution.ts: capture the invite code on the
 * landing hit — from the `/open/invite/<code>` share path or a `?ref=<code>` query — and hand it
 * to whichever sign-in path the visitor eventually finishes.
 * Same code format, same 7-day TTL, same storage key shape as the app.
 */

const STORAGE_KEY = "muhaha.referral.pending.v1";
const REFERRAL_CODE_RE = /^[ABCDEFGHJKLMNPQRSTUVWXYZ23456789]{6}$/;
/** Canonical share shape, minted by lib/appLinks.ts and loopit-app/lib/shareLinks.ts. */
const INVITE_PATH_RE = /^\/open\/invite\/([^/?#]+)/;

/** Referral attribution expires after a week if the visitor does not finish registration. */
export const PENDING_REFERRAL_TTL_MS = 7 * 24 * 60 * 60 * 1000;

interface PendingReferral {
	code: string;
	capturedAt: number;
	expiresAt: number;
}

/**
 * localStorage throws (not just returns null) in some privacy modes and iframes, so every
 * access is guarded: a storage failure degrades to "this visitor has no referral", never
 * to an exception on the sign-in path.
 */
function storage(): Storage | null {
	try {
		if (typeof window === "undefined") return null;
		return window.localStorage ?? null;
	} catch {
		return null;
	}
}

/** Normalize a share-link segment to Hive's unambiguous six-character referral format. */
export function normalizeReferralCode(raw: string): string | null {
	const code = raw.trim().toUpperCase();
	return REFERRAL_CODE_RE.test(code) ? code : null;
}

export function clearPendingReferralCode(): void {
	try {
		storage()?.removeItem(STORAGE_KEY);
	} catch {
		// Best-effort: leaving a stale record behind is better than breaking sign-in.
	}
}

function validStoredPending(value: unknown, now: number): PendingReferral | null {
	if (!value || typeof value !== "object") return null;
	const candidate = value as Partial<PendingReferral>;
	const code = typeof candidate.code === "string" ? normalizeReferralCode(candidate.code) : null;
	if (
		!code ||
		!Number.isFinite(candidate.capturedAt) ||
		!Number.isFinite(candidate.expiresAt) ||
		(candidate.capturedAt as number) > now ||
		(candidate.expiresAt as number) <= now ||
		(candidate.expiresAt as number) - (candidate.capturedAt as number) !== PENDING_REFERRAL_TTL_MS
	) {
		return null;
	}
	return {
		code,
		capturedAt: candidate.capturedAt as number,
		expiresAt: candidate.expiresAt as number,
	};
}

/**
 * Persist a code with a fresh TTL window. Returns the normalized code, or null when it is
 * unusable (malformed, or storage refuses the write) — in which case any previously captured
 * code is left untouched.
 */
function storePendingReferral(raw: string, now: number): string | null {
	const code = normalizeReferralCode(raw);
	if (!code) return null;

	const pending: PendingReferral = {
		code,
		capturedAt: now,
		expiresAt: now + PENDING_REFERRAL_TTL_MS,
	};
	try {
		const store = storage();
		if (!store) return null;
		store.setItem(STORAGE_KEY, JSON.stringify(pending));
	} catch {
		return null;
	}
	return code;
}

/**
 * Read `?ref=` off a landing query and persist it for later sign-in. Returns the normalized
 * code, or null when the query carries no usable referral (missing, malformed, or unstorable).
 */
export function captureReferralFromSearch(search: string, now = Date.now()): string | null {
	let raw: string | null;
	try {
		raw = new URLSearchParams(search).get("ref");
	} catch {
		return null;
	}
	if (raw === null) return null;
	return storePendingReferral(raw, now);
}

/**
 * Read the code off an `/open/invite/<code>` landing path — the shape every shared invite link
 * actually uses. The router forwards that route to `/?ref=<code>`, but client-side, long after
 * main.tsx has read the query, so the path is the only place the code is still visible on the
 * landing hit. Returns null when the path is not an invite link or its code is unusable.
 */
export function captureReferralFromPath(pathname: string, now = Date.now()): string | null {
	const segment = INVITE_PATH_RE.exec(pathname)?.[1];
	if (segment === undefined) return null;

	let raw: string;
	try {
		raw = decodeURIComponent(segment);
	} catch {
		return null; // A malformed percent-escape throws rather than returning null.
	}
	return storePendingReferral(raw, now);
}

/** Persist a code the visitor typed by hand, on the same TTL as a captured link. */
export function setPendingReferralCode(raw: string, now = Date.now()): string | null {
	return storePendingReferral(raw, now);
}

/**
 * The pending referral code, if one was captured within the TTL. Expired or corrupt records
 * are dropped on read so they can never ride along with a much later registration.
 */
export function peekPendingReferralCode(now = Date.now()): string | null {
	let raw: string | null;
	try {
		raw = storage()?.getItem(STORAGE_KEY) ?? null;
	} catch {
		return null;
	}
	if (raw === null) return null;

	let parsed: unknown;
	try {
		parsed = JSON.parse(raw);
	} catch {
		clearPendingReferralCode();
		return null;
	}

	const pending = validStoredPending(parsed, now);
	if (!pending) {
		clearPendingReferralCode();
		return null;
	}
	return pending.code;
}
